import { z } from 'zod';
import { orgRoleSchema } from '../enums.js';
/**
 * Contrats de la **console d'administration plateforme** (ADR-0025). Ces vues sont **transverses**
 * (elles agrègent toutes les organisations) et strictement réservées au super-administrateur ;
 * aucune n'expose de donnée métier fine, seulement des compteurs et des états d'exploitation.
 */
/** Une organisation vue depuis l'administration : identité + métriques déjà disponibles en base. */
export const adminOrgSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    /** Octets stockés (recalculés à l'ingestion/purge). Sérialisé en nombre — voir ADR-0025. */
    storageUsedBytes: z.number(),
    /** Quota en octets, `null` si illimité. */
    storageQuotaBytes: z.number().nullable(),
    memberCount: z.number().int(),
    videoCount: z.number().int(),
    modelCount: z.number().int(),
    createdAt: z.coerce.date(),
});
/** État d'une dépendance d'exploitation (sonde `/health/ready`). */
export const serviceHealthSchema = z.object({
    database: z.boolean(),
    storage: z.boolean(),
    queue: z.boolean(),
});
/**
 * Vue monitoring : santé des services + profondeur des files de traitement (BullMQ). `queues`
 * associe le nom d'une file au nombre de jobs en attente + actifs (ce qui reste à traiter).
 */
export const adminMonitoringSchema = z.object({
    services: serviceHealthSchema,
    queues: z.record(z.string(), z.number().int()),
    /** Horodatage de la mesure, pour situer une valeur potentiellement en cache côté client. */
    generatedAt: z.coerce.date(),
});
/**
 * Consommation d'une organisation sur la fenêtre demandée (ADR-0025, incrément 2). `aiTokens` en
 * jetons, `dataTransferBytes` en octets — tous deux sérialisés en nombre (registre en `BigInt`).
 */
export const adminOrgConsumptionSchema = z.object({
    organizationId: z.string(),
    name: z.string(),
    slug: z.string(),
    aiTokens: z.number(),
    dataTransferBytes: z.number(),
});
/** Vue consommation : fenêtre observée + total plateforme + détail par organisation (décroissant). */
export const adminConsumptionSchema = z.object({
    /** Nombre de jours couverts par l'agrégat (fenêtre glissante). */
    windowDays: z.number().int(),
    totals: z.object({ aiTokens: z.number(), dataTransferBytes: z.number() }),
    organizations: z.array(adminOrgConsumptionSchema),
    generatedAt: z.coerce.date(),
});
/** Un jour de la série temporelle de consommation (date UTC `YYYY-MM-DD`). */
export const adminConsumptionDaySchema = z.object({
    /** Jour UTC au format `YYYY-MM-DD`. */
    date: z.string(),
    aiTokens: z.number(),
    dataTransferBytes: z.number(),
});
/**
 * Série **journalière** de la consommation plateforme sur la fenêtre (ADR-0025, incrément 3). Les
 * jours sans consommation sont présents et à zéro, du plus ancien au plus récent : une courbe
 * continue, sans trou à interpoler côté client.
 */
export const adminConsumptionSeriesSchema = z.object({
    windowDays: z.number().int(),
    days: z.array(adminConsumptionDaySchema),
    generatedAt: z.coerce.date(),
});
/** Un membre d'une organisation vu depuis l'administration, avec sa consommation sur la fenêtre. */
export const adminMemberConsumptionSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    email: z.string(),
    role: orgRoleSchema,
    aiTokens: z.number(),
    dataTransferBytes: z.number(),
    /** Octets stockés attribués à cet utilisateur (dérivés des vidéos qu'il a téléversées). */
    storageBytes: z.number(),
});
/**
 * Détail d'une organisation (ADR-0025, incrément 3) : identité + membres avec leur consommation par
 * utilisateur sur la fenêtre. Lecture transverse, journalisée (`PLATFORM_ORG_VIEWED`).
 */
export const adminOrgDetailSchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    storageUsedBytes: z.number(),
    storageQuotaBytes: z.number().nullable(),
    createdAt: z.coerce.date(),
    windowDays: z.number().int(),
    members: z.array(adminMemberConsumptionSchema),
    generatedAt: z.coerce.date(),
});
/** Un jour de la série d'utilisateurs actifs (date UTC `YYYY-MM-DD`). */
export const adminActiveDaySchema = z.object({
    date: z.string(),
    users: z.number().int(),
});
/** Un événement d'usage et son nombre d'occurrences sur la fenêtre (top fonctionnalités). */
export const adminFeatureCountSchema = z.object({
    name: z.string(),
    count: z.number().int(),
});
/**
 * Restitution de l'**analytique d'usage** (ADR-0026, incrément 3). Vue **transverse** (toutes
 * organisations), réservée à l'exploitant, journalisée (`PLATFORM_ANALYTICS_VIEWED`). Agrégats
 * uniquement — aucun événement brut, aucune donnée personnelle.
 */
export const adminUsageAnalyticsSchema = z.object({
    windowDays: z.number().int(),
    /** Utilisateurs distincts actifs sur toute la fenêtre. */
    activeUsers: z.number().int(),
    /** Nombre total d'événements sur la fenêtre. */
    eventTotal: z.number().int(),
    /** Utilisateurs distincts actifs par jour (courbe continue, zéros compris). */
    activeDaily: z.array(adminActiveDaySchema),
    /** Fonctionnalités les plus employées, décroissant. */
    topFeatures: z.array(adminFeatureCountSchema),
    /** Tunnel d'upload : combien démarrent, aboutissent, échouent (mesure la friction). */
    uploadFunnel: z.object({
        started: z.number().int(),
        completed: z.number().int(),
        failed: z.number().int(),
    }),
    generatedAt: z.coerce.date(),
});
//# sourceMappingURL=admin.js.map